import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../api/client';
import type { Municipality, RealmInfo } from '../../../api/types';
import { Alert, Badge, Button } from '../../../components/ui';
import {
  emptyForm, fromPayload, newMeterInputId, submitCheck, toPayload,
  type OnboardingForm, type OnboardingRequest, type OnboardingRequestSummary, type SubmitCheckItem,
} from '../../../lib/onboarding';
import OnboardForm from './OnboardForm';
import CommissionPanel from './CommissionPanel';

// Absolute paths: production runs react-router with v7_relativeSplatPath off.
const BASE = '/field-tools/onboard';

type EmailOutcome = 'sent' | 'skipped' | 'failed';

export function OnboardList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OnboardingRequestSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.listOnboardingRequests().then((r) => setRows(r.data)).catch((e: Error) => setError(e.message));
  }, []);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 text-sm text-content-muted">Onboard a client into a realm that already exists.</p>
        <Button onClick={() => navigate(`${BASE}/new`)}>New request</Button>
      </div>
      {error && <Alert tone="danger" title="Could not load requests">{error}</Alert>}
      {rows && rows.length === 0 && <p className="text-sm text-content-muted">No requests yet.</p>}
      <ul className="space-y-2">
        {rows?.map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => navigate(`${BASE}/${r.realm}/${r.id}`)}
              className="tap-target w-full justify-between gap-3 rounded-lg border border-border-token bg-surface-raised px-4 py-3 text-left">
              <span className="min-w-0">
                <span className="block truncate font-medium text-content">{r.friendly_name || r.realm}</span>
                <span className="block text-xs text-content-muted">
                  {r.realm}: {r.counts.sites} site(s), {r.counts.gateways} gateway(s), {r.counts.meters} meter(s)
                </span>
              </span>
              <Badge tone={r.status === 'submitted' ? 'success' : 'neutral'}>{r.status === 'submitted' ? 'Submitted' : 'Draft'}</Badge>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OnboardEditor({ initial, onSubmitted }: {
  initial?: OnboardingRequest;
  onSubmitted?: (r: OnboardingRequest, email: EmailOutcome) => void;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState<OnboardingForm>(() => (initial ? fromPayload(initial.payload) : emptyForm()));
  const [id, setId] = useState<string | null>(initial?.id ?? null);
  const [realms, setRealms] = useState<RealmInfo[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Submit's check: gateways without meters and sites without gateways. Any edit clears it, so the
  // next Submit checks the form as it is then.
  const [check, setCheck] = useState<SubmitCheckItem[] | null>(null);
  const edit = (next: OnboardingForm) => { setForm(next); setCheck(null); };

  useEffect(() => {
    api.getRealms().then((r) => setRealms(r.data)).catch((e: Error) => setError(`Realms: ${e.message}`));
    api.getMunicipalities().then((r) => setMunicipalities(r.data)).catch((e: Error) => setError(`Municipalities: ${e.message}`));
  }, []);

  const save = async (): Promise<string | null> => {
    const { payload, blocking } = toPayload(form);
    if (blocking.length) { setProblems(blocking); return null; }
    if (!payload.realm) { setProblems(['Pick a realm']); return null; }
    const r = id ? await api.updateOnboardingRequest(id, payload) : await api.createOnboardingRequest(payload);
    setId(r.data.id);
    return r.data.id;
  };

  const onSave = async () => {
    setBusy(true); setError(null); setProblems([]); setNote(null); setCheck(null);
    try {
      const saved = await save();
      if (saved) {
        setNote('Draft saved');
        if (!initial) navigate(`${BASE}/${form.realm}/${saved}`, { replace: true });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (anyway = false) => {
    const { errors } = toPayload(form);
    if (errors.length) { setProblems(errors); setCheck(null); return; }
    const items = anyway ? [] : submitCheck(form);
    if (items.length) { setProblems([]); setError(null); setNote(null); setCheck(items); return; }
    setBusy(true); setError(null); setProblems([]); setNote(null); setCheck(null);
    try {
      const saved = await save();
      if (!saved) return;
      const r = await api.submitOnboardingRequest(saved, form.realm);
      if (onSubmitted) onSubmitted(r.data, r.email);
      // From New request: the request page remounts, so the outcome travels with the navigation.
      else navigate(`${BASE}/${form.realm}/${saved}`, { replace: true, state: { email: r.email } });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <OnboardForm value={form} onChange={edit} realms={realms} municipalities={municipalities} realmLocked={id !== null} />
      {problems.length > 0 && (
        <Alert tone="warning" title="Fix these first">
          <ul className="list-disc pl-5 space-y-1">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
        </Alert>
      )}
      {check && <SubmitCheck items={check} busy={busy} onSubmitAnyway={() => void onSubmit(true)} />}
      {error && <Alert tone="danger" title="Not saved">{error}</Alert>}
      {note && <Alert tone="success">{note}</Alert>}
      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" disabled={busy} onClick={() => void onSave()}>Save draft</Button>
        <Button disabled={busy} onClick={() => void onSubmit()}>Submit</Button>
      </div>
    </div>
  );
}

/** Allowed, but usually a slip: the request that reached Beau with no gateways and no meters. Lists a
 *  request with no sites, a site with no gateways and a gateway with no meters. */
function SubmitCheck({ items, busy, onSubmitAnyway }: { items: SubmitCheckItem[]; busy: boolean; onSubmitAnyway: () => void }) {
  const gateways = items.filter((i): i is Extract<SubmitCheckItem, { kind: 'gateway' }> => i.kind === 'gateway');
  // Scroll the gateway's new-meter row into view and put the cursor in it.
  const addMeters = (gatewayKey: string) => {
    const el = document.getElementById(newMeterInputId(gatewayKey));
    if (!el) return;
    el.scrollIntoView?.({ block: 'center' });
    el.focus({ preventScroll: true });
  };
  return (
    <Alert tone="warning" title="Before you submit">
      <ul className="list-disc pl-5 space-y-1 break-words">
        {items.map((i) => (
          <li key={i.key}>
            {i.kind === 'request' ? 'This request has no sites.'
              : i.kind === 'site' ? `Site ${i.building} has no gateways.`
                : `Gateway ${i.serial} (${i.utility})${i.room ? ` in ${i.room}` : ''} has no meters.`}
          </li>
        ))}
      </ul>
      {gateways.length > 0 && <p className="mt-2">A gateway without meters is allowed: meters can be commissioned later.</p>}
      <div className="mt-3 flex flex-col lg:flex-row lg:flex-wrap gap-2">
        {gateways.map((g) => (
          <Button key={g.key} className="max-w-full [overflow-wrap:anywhere]" onClick={() => addMeters(g.key)}>Add meters to {g.serial}</Button>
        ))}
        <Button variant="secondary" disabled={busy} onClick={onSubmitAnyway}>Submit anyway</Button>
      </div>
    </Alert>
  );
}

function RequestSummary({ request }: { request: OnboardingRequest }) {
  const p = request.payload;
  // The name, once found; the id stands in for it until then, or if it cannot be found.
  const [municipality, setMunicipality] = useState<string | null>(p.municipality_id);
  useEffect(() => {
    let cancelled = false;
    setMunicipality(p.municipality_id);
    if (p.municipality_id) {
      api.getMunicipalities()
        .then((r) => {
          const found = r.data.find((m) => m.id === p.municipality_id);
          if (!cancelled && found) setMunicipality(found.name);
        })
        .catch(() => { /* keep the id */ });
    }
    return () => { cancelled = true; };
  }, [p.municipality_id]);
  return (
    <section className="bg-surface-raised rounded-xl border border-border-token px-4 lg:px-6 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="min-w-0 truncate text-base font-semibold text-content">{p.friendly_name} ({request.realm})</h2>
        <Badge tone="success">Submitted</Badge>
      </div>
      <p className="text-sm text-content-muted break-words">
        Operator: <code>python3 -m onboard new {request.id}</code> for a new realm, or <code>python3 -m onboard merge {request.id}</code> for an existing one. Both are dry runs until given <code>--apply --confirm {request.realm}</code>.
      </p>
      {p.sites.map((s) => (
        <div key={`${s.city}/${s.building}`} className="text-sm text-content space-y-1">
          <p className="font-medium">{s.city} &gt; {s.building}</p>
          <ul className="pl-4 space-y-1">
            {s.gateways.map((g) => (
              <li key={g.serial} className="break-words">
                {g.room} &gt; {g.group} ({g.utility}, {g.connection === 'pi' ? 'Pi gateway' : `modems ${g.modems.join(', ')}`}): {g.meters.length} meter(s)
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="text-sm text-content">{p.users.length} user(s), {p.pockets.length} pocket(s)</p>
      <p className="text-sm text-content break-words">Municipality: <span>{municipality ?? 'Not set'}</span></p>
      <div className="text-sm text-content space-y-1">
        <p className="font-medium">Notes</p>
        {p.notes.trim()
          ? <p className="whitespace-pre-wrap break-words">{p.notes}</p>
          : <p className="text-content-muted">No notes.</p>}
      </div>
    </section>
  );
}

export function OnboardRequestPage() {
  const { realm = '', id = '' } = useParams();
  // A submit from New request arrives here by navigation, carrying its notification outcome.
  const arrivedWith = (useLocation().state as { email?: EmailOutcome } | null)?.email ?? null;
  const [request, setRequest] = useState<OnboardingRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<EmailOutcome | null>(arrivedWith);
  useEffect(() => {
    let cancelled = false;
    setRequest(null);
    setError(null);
    setEmail(arrivedWith);
    api.getOnboardingRequest(id, realm)
      .then((r) => { if (!cancelled) setRequest(r.data); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [realm, id, arrivedWith]);
  if (error) return <Alert tone="danger" title="Could not load the request">{error}</Alert>;
  if (!request) return <p className="text-sm text-content-muted">Loading...</p>;
  if (request.status === 'draft') {
    return <OnboardEditor key={request.id} initial={request} onSubmitted={(r, e) => { setRequest(r); setEmail(e); }} />;
  }
  return (
    <div className="space-y-6">
      {email === 'sent' && <Alert tone="success">Submitted. Beau has been notified.</Alert>}
      {email && email !== 'sent' && (
        <Alert tone="warning" title="Submitted, but the notification did not go out">Tell Beau the request id: {request.id}</Alert>
      )}
      <RequestSummary request={request} />
      <CommissionPanel request={request} />
    </div>
  );
}

export default function OnboardRoutes() {
  return (
    <Routes>
      <Route index element={<OnboardList />} />
      <Route path="new" element={<OnboardEditor />} />
      <Route path=":realm/:id" element={<OnboardRequestPage />} />
    </Routes>
  );
}
